import React, { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Button, useTranslate } from 'react-admin';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { SvgIcon } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ApprovalCaseDetail, ApprovalCaseStep } from '~/types/approvalCase';
import { time } from '~/utils';
import { useReferenceEntity } from '~/hooks';
import { getTypeOfIdentity } from '~/utils/identities';
import palette from '~/theme/palette';
import useEventEntity from '~/hooks/eventEntities';
import { ArrowForwardCircleIcon } from '~/img';

type IconState = 'completed' | 'active' | 'pending' | 'rejected' | 'canceled';

const ICON_SIZE = 20;

const colorMap: Record<IconState, string> = {
  completed: palette.success.main,
  rejected: palette.error.main,
  pending: palette.divider,
  active: palette.warning.main,
  canceled: palette.warning.main,
};

const dateFormat = (iso: string) => time(iso).format('l, LTS');

interface Props {
  record: ApprovalCaseDetail;
  resource?: string;
}

export const ApprovalProgress: FC<Props> = ({ record, resource }) => {
  const translate = useTranslate();
  const t = (key: string, opts?: object) => translate(l(key), opts);
  const classes = useStyles();

  const { steps, status, submittedAt, originatorIdentityId, originatorIdentityType } = record;

  const rejectedCount = steps.filter((s) => s.status === 'REJECTED').length;
  const activeFlowIdx = steps.findIndex((s) => s.status === 'PENDING');

  const totalSteps = steps.length + 2;
  const activeStepperIdx = activeFlowIdx !== -1 ? activeFlowIdx + 1 : totalSteps;

  const getIconState = (idx: number): IconState => {
    if (idx === 0) return 'completed';
    if (idx < activeStepperIdx) {
      if (idx < totalSteps - 1)
        return steps[idx - 1].status === 'REJECTED' ? 'rejected' : 'completed';
      return status === 'APPROVED'
        ? 'completed'
        : status === 'FAILED'
        ? 'rejected'
        : status === 'CANCELLED'
        ? 'canceled'
        : 'pending';
    }
    if (idx === activeStepperIdx) {
      if (status === 'FAILED') return 'rejected';
      if (status === 'CANCELLED') return 'canceled';
      if (rejectedCount === 0) return 'active';
      return 'pending';
    }
    return 'pending';
  };

  const getLabel = (idx: number): ReactNode => {
    if (idx === 0) return t('submitted');
    if (idx === totalSteps - 1) return t('completed');
    const step = steps[idx - 1];
    if (step.roles.length === 0) return step.name;
    return <StepLabelWithRoles step={step} />;
  };

  const getSublabel = (idx: number): ReactNode => {
    if (idx === 0) {
      return (
        <>
          <IdentityName identityId={originatorIdentityId} identityType={originatorIdentityType} />
          {` · ${dateFormat(submittedAt)}`}
        </>
      );
    }
    if (idx === totalSteps - 1) return undefined;
    const step = steps[idx - 1];
    if (step.status !== 'PENDING' && step.decidedAt) {
      const decision = step.status === 'APPROVED' ? t('approvedBy') : t('rejectedBy');
      const decidedById = step.decidedByIdentityId;
      const decidedByType = step.decidedByIdentityType;
      const decidedAt = step.decidedAt;

      return (
        <>
          {`${decision} `}
          {decidedById && decidedByType && (
            <IdentityName identityId={decidedById} identityType={decidedByType} />
          )}
          {` · ${dateFormat(decidedAt)}`}
        </>
      );
    }
    if (idx === activeStepperIdx && status === 'IN_PROGRESS') return t('awaitingDecision');
    return undefined;
  };

  const getComment = (idx: number): string | undefined => {
    if (idx === 0 || idx === totalSteps - 1) return undefined;
    return steps[idx - 1].note?.trim() || undefined;
  };

  const isApprovalResource = resource === 'approvals/queue' || resource === 'approvals/history';

  return (
    <>
      <Box className={classes.header}>
        <Typography variant='h6'>{t('progress')}</Typography>
        {status === 'IN_PROGRESS' && isApprovalResource && (
          <Typography variant='caption'>
            {t('approvalsRequired', { completed: record.currentStep, total: steps.length })}
          </Typography>
        )}
        {!isApprovalResource && (
          <Button
            label={t('viewCase')}
            href={`/approvals/queue/${record.id}/show`}
            target='_blank'
            onClick={(e: any) => e.stopPropagation()}
          >
            <SvgIcon>
              <ArrowForwardCircleIcon />
            </SvgIcon>
          </Button>
        )}
      </Box>
      <Divider className={classes.divider} />
      <Box className={classes.stepsContainer}>
        {Array.from({ length: totalSteps }).map((_, idx) => {
          const sublabel = getSublabel(idx);
          const comment = getComment(idx);
          const isLast = idx === totalSteps - 1;
          const iconState = getIconState(idx);
          return (
            <div key={idx} className={classes.stepRow}>
              <div className={classes.iconCol}>
                <FlowStepIcon state={iconState} />
                {!isLast && (
                  <div
                    className={classes.connector}
                    style={{
                      backgroundColor:
                        iconState === 'completed' ? colorMap.completed : colorMap.pending,
                    }}
                  />
                )}
              </div>
              <div className={classes.labelCol}>
                <Typography variant='body2' className={classes.stepLabel}>
                  {getLabel(idx)}
                </Typography>
                {sublabel && (
                  <Typography variant='caption' className={classes.sublabel}>
                    {sublabel}
                  </Typography>
                )}
                {comment && <StepComment comment={comment} />}
              </div>
            </div>
          );
        })}
      </Box>
    </>
  );
};

const StepComment: FC<{ comment: string }> = ({ comment }) => {
  const translate = useTranslate();
  const t = (key: string, opts?: object) => translate(l(key), opts);
  const classes = useStyles();
  const textRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [comment]);

  return (
    <div className={classes.comment}>
      <Typography
        ref={textRef}
        variant='caption'
        component='span'
        className={expanded ? classes.commentTextExpanded : classes.commentText}
      >
        {comment}
      </Typography>
      {(isOverflowing || expanded) && (
        <Typography
          variant='caption'
          component='button'
          type='button'
          aria-expanded={expanded}
          className={classes.commentToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('hide') : t('showMore')}
        </Typography>
      )}
    </div>
  );
};

const FlowStepIcon: FC<{ state: IconState }> = ({ state }) => {
  const classes = useStyles();
  if (state === 'active') {
    return (
      <div className={classes.activeFlowStepOuter}>
        <div className={classes.activeFlowStepInner} />
      </div>
    );
  }
  return <div className={classes.flowStep} style={{ backgroundColor: colorMap[state] }} />;
};

interface StepLabelWithRolesProps {
  step: ApprovalCaseStep;
}

const StepLabelWithRoles: FC<StepLabelWithRolesProps> = ({ step }) => {
  const { name, roles } = step;
  return (
    <>
      {name}
      <span style={{ fontWeight: 'normal' }}>
        {' ('}
        {roles.map((roleId, idx) => (
          <React.Fragment key={roleId}>
            <RoleName roleId={roleId} />
            {idx < roles.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}
        {')'}
      </span>
    </>
  );
};

const RoleName: FC<{ roleId: string }> = ({ roleId }) => {
  const translate = useTranslate();
  const t = (key: string, opts?: object) => translate(l(key), opts);
  const { data, isLoading } = useEventEntity({ id: roleId, type: 'DASHBOARD_ROLE' });

  if (isLoading) {
    return <>{t('loading')}</>;
  }
  return <>{data?.name ?? roleId}</>;
};

const IdentityName: FC<{ identityId: string; identityType: string }> = ({
  identityId,
  identityType,
}) => {
  const translate = useTranslate();
  const t = (key: string, opts?: object) => translate(l(key), opts);
  const { data, isLoading } = useReferenceEntity({
    id: identityId,
    type: getTypeOfIdentity(identityType),
  });
  if (isLoading) {
    return <>{t('loading')}</>;
  }
  return <>{data?.name ?? identityId}</>;
};

const l = (key: string): string => `resources.approvals.show.${key}`;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  divider: {
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
  stepsContainer: {
    paddingTop: theme.spacing(2),
  },
  stepRow: {
    display: 'flex',
  },
  iconCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: ICON_SIZE,
    marginRight: 12,
  },
  connector: {
    flex: '1 1 auto',
    width: 2,
    minHeight: theme.spacing(3),
    marginTop: 2,
    marginBottom: 2,
    opacity: 0.6,
  },
  labelCol: {
    flex: 1,
    paddingBottom: 12,
  },
  stepLabel: {
    fontWeight: 600,
    lineHeight: `${ICON_SIZE}px`,
  },
  sublabel: {
    display: 'block',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  comment: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  commentText: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  commentTextExpanded: {
    display: 'block',
  },
  commentToggle: {
    display: 'block',
    marginTop: 2,
    padding: 0,
    border: 0,
    background: 'none',
    textAlign: 'left',
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  flowStep: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: '50%',
    flexShrink: 0,
  },
  activeFlowStepOuter: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: '50%',
    border: `2px solid ${colorMap.active}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  activeFlowStepInner: {
    width: ICON_SIZE / 2 - 2,
    height: ICON_SIZE / 2 - 2,
    borderRadius: '50%',
    backgroundColor: colorMap.active,
  },
}));
